import { useQuery } from '@tanstack/react-query';
import { DateRange } from '@Components/DatePicker/date-picker-types';
import { awaitJSON, ToolsLambdaFetch } from '@src/utils/fetch-utils';
import {
  CompanyOption,
  DeviceBreakoutCompanyData,
  DeviceBreakoutNetworkData,
  FlightData,
  NetworkOption
} from './device-breakout-constants';

export const useCompanyOptions = (): ReturnType<typeof useQuery<CompanyOption[]>> =>
  useQuery({
    queryKey: ['deviceBreakoutCompanyOptions'],
    queryFn: async (): Promise<CompanyOption[]> =>
      awaitJSON<CompanyOption[]>(await ToolsLambdaFetch('/activeott_company_options'))
  });

export const useNetworkOptions = (dates: DateRange): ReturnType<typeof useQuery<NetworkOption[]>> =>
  useQuery({
    queryKey: ['deviceBreakoutNetworkOptions', dates],
    enabled: Boolean(dates.start && dates.end),
    queryFn: async (): Promise<NetworkOption[]> =>
      awaitJSON<NetworkOption[]>(
        await ToolsLambdaFetch('/device_breakout_network_options', { params: dates })
      )
  });

export const useDeviceBreakoutByCompany = (
  dates: DateRange,
  company: string | false
): ReturnType<typeof useQuery<DeviceBreakoutCompanyData>> =>
  useQuery({
    queryKey: ['deviceBreakoutByCompany', dates, company],
    enabled: Boolean(company),
    queryFn: async (): Promise<DeviceBreakoutCompanyData> =>
      awaitJSON<DeviceBreakoutCompanyData>(
        await ToolsLambdaFetch('/device_breakout', { params: { ...dates, company } })
      )
  });

export const useDeviceBreakoutByNetwork = (
  dates: DateRange,
  network: string | false,
  platform: string
): ReturnType<typeof useQuery<DeviceBreakoutNetworkData>> =>
  useQuery({
    queryKey: ['deviceBreakoutByNetwork', dates, network, platform],
    enabled: Boolean(network),
    queryFn: async (): Promise<DeviceBreakoutNetworkData> =>
      awaitJSON<DeviceBreakoutNetworkData>(
        await ToolsLambdaFetch('/device_breakout', { params: { ...dates, network, platform } })
      )
  });

// The flight-drill-down modal only fetches in company mode.
export const useCompanyFlightData = (
  dates: DateRange,
  company: string | false
): ReturnType<typeof useQuery<FlightData>> =>
  useQuery({
    queryKey: ['deviceBreakoutFlightData', dates, company],
    enabled: Boolean(company),
    queryFn: async (): Promise<FlightData> =>
      awaitJSON<FlightData>(await ToolsLambdaFetch('/activeott', { params: { ...dates, company } }))
  });
